import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, inr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Plus, Trash, PencilSimple, Money, ArrowsClockwise, CheckCircle,
  Warning, HourglassMedium, Receipt, StackSimple, Printer, EnvelopeSimple, Lock, WhatsappLogo
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LineItemsEditor from "@/components/LineItemsEditor";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import { SupportingTendersEditor } from "@/components/SupportingTendersEditor";
import { Checkbox } from "@/components/ui/checkbox";
import { downloadCSV } from "@/lib/reportExport";
import { MicrosoftExcelLogo } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending:   { label: "Pending",   className: "bg-warning/10 text-warning border-warning/30" },
  partial:   { label: "Partial",   className: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
  paid:      { label: "Fully Paid", className: "bg-success/10 text-success border-success/30" },
  converted: { label: "Converted", className: "bg-primary/10 text-primary border-primary/30" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10.5px] uppercase tracking-wider font-semibold ${s.className}`}>
      {status === "paid" && <CheckCircle size={11} weight="fill" />}
      {status === "converted" && <CheckCircle size={11} weight="fill" />}
      {status === "pending" && <HourglassMedium size={11} weight="fill" />}
      {status === "partial" && <ArrowsClockwise size={11} weight="fill" />}
      {s.label}
    </span>
  );
}

const emptyForm = {
  date: new Date().toISOString().slice(0, 10),
  customer_id: "", customer_name: "", customer_gstin: "", customer_state_code: "",
  items: [], notes: "", supporting_tenders: [],
};

export default function PendingPayments() {
  const { activeCompanyId, can, activeCompany, user, activeStoreId, dateFloor } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [q, setQ] = useState("");
  const [filterCustomerId, setFilterCustomerId] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterItem, setFilterItem] = useState("");

  // Force re-render every 2 hours so the "Call for Payment" alert refreshes
  // even when the browser tab is left open.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 2 * 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    date: new Date().toISOString().slice(0, 10), amount: 0, mode: "cash", reference: ""
  });

  // Multi-select for merge-convert
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  useEffect(() => { clearSelection(); }, [filterStatus, q, filterCustomerId, filterFrom, filterTo, filterItem, activeCompanyId, activeStoreId]);

  const isGst = !!(activeCompany?.gstin || "").trim();
  const companyStateCode = activeCompany?.state_code || "";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (q.trim()) params.set("q", q.trim());
      const [t, c, p, s] = await Promise.all([
        api.get(`/temp-invoices?${params}`),
        api.get("/customers"),
        api.get("/products"),
        api.get("/services"),
      ]);
      setRows(t.data);
      setCustomers(c.data);
      setProducts(p.data);
      setServices(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, filterStatus, q]);

  const startAdd = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      items: [{ product_id: "", name: "", hsn: "", quantity: 1, unit: "PCS", rate: 0, discount_pct: 0, tax_rate: isGst ? 18 : 0 }],
    });
    setOpen(true);
  };
  const startEdit = (r) => {
    setEditing(r);
    setForm({
      date: r.date,
      customer_id: r.customer_id,
      customer_name: r.customer_name,
      customer_gstin: r.customer_gstin || "",
      customer_state_code: r.customer_state_code || "",
      items: r.items || [],
      notes: r.notes || "",
      supporting_tenders: r.supporting_tenders || [],
    });
    setOpen(true);
  };

  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setForm((f) => ({
      ...f,
      customer_id: cid,
      customer_name: c?.name || "",
      customer_gstin: c?.gstin || "",
      customer_state_code: c?.state_code || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Please pick a customer"); return; }
    if (!form.items?.length) { toast.error("Add at least one line item"); return; }
    try {
      if (editing) {
        await api.patch(`/temp-invoices/${editing.id}`, form);
        toast.success("Pending invoice updated");
      } else {
        const { data } = await api.post("/temp-invoices", form);
        toast.success(`Created ${data.number}`);
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openReceipt = (r) => {
    setReceiptTarget(r);
    setReceiptForm({
      date: new Date().toISOString().slice(0, 10),
      amount: r.balance_amount || 0,
      mode: "cash", reference: "",
    });
    setReceiptOpen(true);
  };
  const submitReceipt = async (e) => {
    e.preventDefault();
    if (!receiptTarget) return;
    if (!(receiptForm.amount > 0)) { toast.error("Amount must be positive"); return; }
    try {
      await api.post(`/temp-invoices/${receiptTarget.id}/add-receipt`, receiptForm);
      toast.success(`₹${Number(receiptForm.amount).toFixed(2)} recorded against ${receiptTarget.number}`);
      setReceiptOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const convertToInvoice = async (r) => {
    if (!confirm(`Convert ${r.number} → real Sales Invoice? This action is final.`)) return;
    try {
      const { data } = await api.post(`/temp-invoices/${r.id}/convert`);
      toast.success(`Converted to ${data.invoice_number}. Auto-receipt ${data.voucher_number} recorded.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Merge multiple paid temps for SAME customer into ONE sales invoice
  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds]
  );
  const selectionCustomerId = selectedRows.length ? selectedRows[0].customer_id : "";
  const selectionValid = selectedRows.length >= 1
    && selectedRows.every((r) => r.status !== "converted" && r.customer_id === selectionCustomerId);
  const selectionTotal = selectedRows.reduce((s, r) => s + Number(r.grand_total || 0), 0);
  const selectionBalance = selectedRows.reduce((s, r) => s + Number(r.balance_amount || 0), 0);
  const allPaid = selectedRows.length > 0 && selectedRows.every((r) => r.status === "paid");

  // Merge dialog state (asks for payment info when balances exist)
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeForm, setMergeForm] = useState({
    payment_mode: "cash",
    payment_reference: "",
    payment_date: new Date().toISOString().slice(0, 10),
  });

  const openMerge = () => {
    if (!selectionValid) return;
    setMergeForm({
      payment_mode: "cash",
      payment_reference: "",
      payment_date: new Date().toISOString().slice(0, 10),
    });
    setMergeOpen(true);
  };

  const runMerge = async () => {
    const payload = { ids: Array.from(selectedIds) };
    if (!allPaid) {
      payload.pay_now = true;
      payload.payment_mode = mergeForm.payment_mode;
      payload.payment_reference = mergeForm.payment_reference;
      payload.payment_date = mergeForm.payment_date;
    }
    try {
      const { data } = await api.post("/temp-invoices/merge-convert", payload);
      const noun = data.merged_count === 1 ? "Converted" : `Merged ${data.merged_count} temps`;
      toast.success(`${noun} → ${data.invoice_number} (₹${Number(data.total_amount).toFixed(2)}). Voucher ${data.voucher_number} recorded.`, { duration: 7000 });
      setMergeOpen(false); clearSelection(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const mergeConvert = () => openMerge();

  const del = async (row) => {
    const isConverted = row.status === "converted";
    const msg = isConverted
      ? `Delete ${row.number} AND its converted sales invoice ${row.converted_invoice_number || ""}? This cascades the delete to the Sales Invoice and auto-receipt — manual receipts are kept. This cannot be undone.`
      : "Delete this pending invoice? This cannot be undone.";
    if (!confirm(msg)) return;
    try {
      const { data } = await api.delete(`/temp-invoices/${row.id}`);
      if (data?.cascade?.sales_invoice) {
        toast.success(`Deleted · also removed sales invoice ${data.cascade.sales_invoice}`);
      } else {
        toast.success("Deleted");
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Client-side filters (customer / date range / item name)
  const filteredRows = useMemo(() => {
    const from = (filterFrom || "").trim();
    const to = (filterTo || "").trim();
    const itemQ = (filterItem || "").trim().toLowerCase();
    return rows.filter((r) => {
      if (filterCustomerId !== "all" && r.customer_id !== filterCustomerId) return false;
      if (from && (r.date || "") < from) return false;
      if (to && (r.date || "") > to) return false;
      if (itemQ) {
        const items = (r.items || []).map((i) => (i.name || i.description || "").toLowerCase());
        const hit = items.some((n) => n.includes(itemQ));
        if (!hit) return false;
      }
      return true;
    });
  }, [rows, filterCustomerId, filterFrom, filterTo, filterItem]);

  const totals = useMemo(() => {
    return filteredRows.reduce((acc, r) => {
      acc.total += Number(r.grand_total || 0);
      acc.received += Number(r.received_amount || 0);
      acc.balance += Number(r.balance_amount || 0);
      return acc;
    }, { total: 0, received: 0, balance: 0 });
  }, [filteredRows]);

  const resetFilters = () => {
    setQ(""); setFilterStatus("all"); setFilterCustomerId("all");
    setFilterFrom(""); setFilterTo(""); setFilterItem("");
  };

  const exportExcel = () => {
    if (!filteredRows.length) { toast.error("Nothing to export"); return; }
    const custById = new Map(customers.map((c) => [c.id, c]));
    const now = new Date();
    const rowsForExport = filteredRows.map((r) => {
      const cust = custById.get(r.customer_id) || {};
      const d = new Date(r.date);
      const days = isNaN(d) ? "" : Math.floor((now - d) / 86400000);
      const items = (r.items || []).map((i) => `${i.name || ""} × ${i.quantity || 1} @ ₹${i.rate || 0}`).join(" | ");
      return {
        number: r.number, date: r.date, customer_name: r.customer_name,
        mobile: cust.phone || "", gstin: cust.gstin || "",
        items, subtotal: (r.subtotal || 0).toFixed(2),
        tax: ((r.cgst_total || 0) + (r.sgst_total || 0) + (r.igst_total || 0)).toFixed(2),
        grand_total: (r.grand_total || 0).toFixed(2),
        received: (r.received_amount || 0).toFixed(2),
        balance: (r.balance_amount || 0).toFixed(2),
        status: r.status, age_days: days,
        converted_invoice_number: r.converted_invoice_number || "",
        notes: r.notes || "",
        supporting_company: (r.supporting_tenders || []).join(" | "),
      };
    });
    const columns = [
      { key: "number", label: "Temp #" }, { key: "date", label: "Date" },
      { key: "customer_name", label: "Customer" }, { key: "mobile", label: "Mobile" },
      { key: "gstin", label: "GSTIN" }, { key: "items", label: "Items" },
      { key: "subtotal", label: "Subtotal" }, { key: "tax", label: "Tax" },
      { key: "grand_total", label: "Total" }, { key: "received", label: "Received" },
      { key: "balance", label: "Balance" }, { key: "status", label: "Status" },
      { key: "age_days", label: "Age (days)" },
      { key: "converted_invoice_number", label: "Converted Invoice" },
      { key: "notes", label: "Notes" },
      { key: "supporting_company", label: "Supporting Company Name" },
    ];
    downloadCSV(`payment-pending-${new Date().toISOString().slice(0, 10)}`, columns, rowsForExport);
    toast.success(`Exported ${rowsForExport.length} row${rowsForExport.length > 1 ? "s" : ""}`);
  };

  // Client-facing PDF-style "Statement" for the currently filtered temp invoices.
  // Opens a new tab with a print-ready HTML doc and triggers the browser Print dialog.
  const printStatement = () => {
    if (!filteredRows.length) { toast.error("Nothing to print"); return; }
    const custById = new Map(customers.map((c) => [c.id, c]));
    const now = new Date();
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Group by customer
    const groups = new Map();
    for (const r of filteredRows) {
      const key = r.customer_id || "unknown";
      if (!groups.has(key)) groups.set(key, { customer: custById.get(key) || { name: r.customer_name }, rows: [] });
      groups.get(key).rows.push(r);
    }
    const totalGrand = filteredRows.reduce((s, r) => s + Number(r.grand_total || 0), 0);
    const totalReceived = filteredRows.reduce((s, r) => s + Number(r.received_amount || 0), 0);
    const totalBalance = filteredRows.reduce((s, r) => s + Number(r.balance_amount || 0), 0);

    const rowHtml = (r) => {
      const d = new Date(r.date);
      const days = isNaN(d) ? "" : Math.floor((now - d) / 86400000);
      const items = (r.items || []).map((i) => `${esc(i.name || "")} × ${i.quantity || 1} @ ${fmtInr(i.rate)}`).join("<br>");
      const supp = Array.isArray(r.supporting_tenders) ? r.supporting_tenders.filter(Boolean) : [];
      const suppCell = supp.length
        ? supp.map((s, i) => `<div>${supp.length > 1 ? `<b>Co ${i + 1}:</b> ` : ""}${esc(s)}</div>`).join("")
        : "—";
      return `<tr>
        <td class="mono">${esc(r.number)}</td>
        <td class="mono">${esc(r.date)}</td>
        <td>${items || "—"}</td>
        <td class="supp">${suppCell}</td>
        <td class="num">${fmtInr(r.grand_total)}</td>
        <td class="num pos">${fmtInr(r.received_amount)}</td>
        <td class="num neg">${fmtInr(r.balance_amount)}</td>
        <td class="num">${days}d</td>
        <td class="cap">${esc(r.status)}</td>
      </tr>`;
    };

    const grpHtml = (g) => {
      const c = g.customer;
      const bal = g.rows.reduce((s, r) => s + Number(r.balance_amount || 0), 0);
      return `<section class="customer">
        <div class="cust-head">
          <div>
            <div class="cust-name">${esc(c.name || "")}</div>
            <div class="cust-meta">
              ${c.gstin ? `GSTIN <span class="mono">${esc(c.gstin)}</span> · ` : ""}
              ${c.phone ? `Mobile <span class="mono">${esc(c.phone)}</span> · ` : ""}
              ${g.rows.length} temp invoice${g.rows.length > 1 ? "s" : ""} · Outstanding <b>${fmtInr(bal)}</b>
            </div>
          </div>
        </div>
        <table class="dt">
          <thead>
            <tr><th>Temp #</th><th>Date</th><th>Item(s)</th><th>Supporting Company</th><th class="num">Total</th><th class="num">Received</th><th class="num">Balance</th><th class="num">Age</th><th>Status</th></tr>
          </thead>
          <tbody>${g.rows.map(rowHtml).join("")}</tbody>
        </table>
      </section>`;
    };

    const companyName = activeCompany?.name || "Company";
    const companyGstin = activeCompany?.gstin || "";
    const companyAddr = activeCompany?.address || "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Payment Statement · ${esc(companyName)}</title>
<style>
  * { box-sizing: border-box }
  body { font-family: -apple-system,Segoe UI,Roboto,sans-serif; color:#111; margin:24px; }
  .doc-head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #111; padding-bottom:12px; margin-bottom:16px; }
  .brand { font-weight:800; font-size:20px; letter-spacing:.02em; }
  .meta { text-align:right; font-size:11px; color:#555 }
  h1 { font-size:16px; margin:0 0 6px 0; letter-spacing:.02em; text-transform:uppercase }
  .customer { margin-bottom:22px; page-break-inside: avoid; }
  .cust-head { display:flex; justify-content:space-between; align-items:flex-end; padding:8px 10px; background:#f5f5f5; border:1px solid #ddd; border-bottom:none; }
  .cust-name { font-weight:700; font-size:13px }
  .cust-meta { font-size:11px; color:#555; margin-top:2px }
  table.dt { width:100%; border-collapse: collapse; font-size:11.5px }
  table.dt th, table.dt td { border:1px solid #ddd; padding:6px 8px; }
  table.dt th { background:#fafafa; text-align:left; font-weight:700 }
  .num { text-align:right; font-variant-numeric: tabular-nums }
  .mono { font-family: ui-monospace, Menlo, monospace; }
  .cap { text-transform: capitalize }
  .supp { font-size:10.5px; color:#333; max-width:220px; line-height:1.35 }
  .pos { color:#137333 }
  .neg { color:#b00020; font-weight:600 }
  .grand { border-top:2px solid #111; margin-top:18px; padding-top:12px; display:flex; justify-content:flex-end; gap:32px; font-size:12.5px }
  .grand div span { display:block; color:#555; font-size:10.5px; text-transform:uppercase; letter-spacing:.05em; }
  .grand div b { font-size:16px; font-weight:800; font-variant-numeric: tabular-nums }
  .foot { margin-top:24px; padding-top:12px; border-top:1px dashed #ccc; font-size:10px; color:#666; text-align:center }
  @media print { body { margin: 10mm } }
</style></head><body>
<div class="doc-head">
  <div>
    <div class="brand">${esc(companyName)}</div>
    <div style="font-size:11px;color:#555">${esc(companyAddr)}</div>
    ${companyGstin ? `<div style="font-size:11px;color:#555">GSTIN <span class="mono">${esc(companyGstin)}</span></div>` : ""}
  </div>
  <div class="meta">
    <h1>Payment Pending Statement</h1>
    <div>Generated: ${now.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
    <div>${filteredRows.length} record(s)</div>
  </div>
</div>
${Array.from(groups.values()).map(grpHtml).join("")}
<div class="grand">
  <div><span>Total Billed</span><b>${fmtInr(totalGrand)}</b></div>
  <div><span>Total Received</span><b class="pos">${fmtInr(totalReceived)}</b></div>
  <div><span>Outstanding</span><b class="neg">${fmtInr(totalBalance)}</b></div>
</div>
<div class="foot">Please make payment against the pending balance at your earliest. · Contact the company for reconciliation.</div>
<script>window.onload = () => setTimeout(() => window.print(), 250);</script>
</body></html>`;
    const win = window.open("", "_blank");
    if (!win) { toast.error("Popup blocked — allow popups to print"); return; }
    // Deliver via Blob URL instead of `document.write` — every user value in
    // the template above is already HTML-escaped so no XSS surface remains.
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    win.location.href = URL.createObjectURL(blob);
    toast.success(`Preparing statement for ${filteredRows.length} record${filteredRows.length > 1 ? "s" : ""}…`);
  };
  const activeFilterCount = [
    filterStatus !== "all", q.trim(), filterCustomerId !== "all",
    filterFrom, filterTo, filterItem.trim(),
  ].filter(Boolean).length;

  // Rows older than 10 days that still owe money → nudge to call the customer.
  // Re-computes on every render, and the 2h `tick` above forces a fresh render.
  const overdueForCall = useMemo(() => {
    const now = new Date();
    const custById = new Map(customers.map((c) => [c.id, c]));
    return rows
      .filter((r) => r.status !== "paid" && r.status !== "converted" && r.balance_amount > 0)
      .map((r) => {
        const d = new Date(r.date);
        const days = isNaN(d) ? 0 : Math.floor((now - d) / 86400000);
        const cust = custById.get(r.customer_id) || {};
        return { ...r, days, mobile: cust.phone || cust.mobile || "" };
      })
      .filter((r) => r.days >= 10)
      .sort((a, b) => b.days - a.days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, customers, tick]);

  const intraState = (form.customer_state_code || "") === (companyStateCode || "");

  // LineItemsEditor accepts a single `products` list with an optional `_kind`
  // tag ("product" vs "service"). Merge both so users can bill either from the
  // same dropdown without a separate picker.
  const productOptions = useMemo(() => [
    ...products.map((p) => ({ ...p, _kind: "product", sale_price: p.sale_price ?? p.price ?? 0 })),
    ...services.map((s) => ({ ...s, _kind: "service", sale_price: s.price ?? s.sale_price ?? 0 })),
  ], [products, services]);

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Sales</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Payment Pending</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Temporary invoices (VT/TEMP/nnnn) for services delivered but not yet paid. Convert to real invoice only after full payment.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="h-9" onClick={printStatement} data-testid="tp-print-statement">
            <Printer size={16} className="mr-1.5" /> Print Statement
          </Button>
          <Button variant="outline" className="h-9" onClick={exportExcel} data-testid="tp-export-excel">
            <MicrosoftExcelLogo size={16} className="mr-1.5" /> Export Excel
          </Button>
          {can("sales", "create") && (
            <Button onClick={startAdd} className="h-9" data-testid="tp-new-btn">
              <Plus size={15} weight="bold" className="mr-1.5" /> New Pending Invoice
            </Button>
          )}
        </div>
      </div>

      {/* 10-day "Call for Payment" alert — re-evaluated every 2 hours */}
      {overdueForCall.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-2" data-testid="tp-call-alert">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="fill" className="text-destructive" />
            <div className="font-heading font-bold text-[15px] text-destructive">
              Call for Payment · {overdueForCall.length} invoice{overdueForCall.length > 1 ? "s" : ""} overdue &gt; 10 days
            </div>
          </div>
          <p className="text-[12px] text-muted-foreground">
            The following customers still owe money on temp invoices older than 10 days. Reminder auto-refreshes every 2 hours.
          </p>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {overdueForCall.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-background rounded-md border border-destructive/20 px-3 py-2"
                   data-testid={`tp-call-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-[13px] truncate">
                    {r.customer_name}
                    <span className="ml-2 font-mono text-[11px] text-muted-foreground">{r.number}</span>
                  </div>
                  <div className="text-[11.5px] text-muted-foreground">
                    <span className="text-destructive font-semibold">{r.days} days old</span> · Balance {inr(r.balance_amount)}
                    {r.mobile ? <> · <span className="font-mono">{r.mobile}</span></> : <span className="italic"> · no mobile on file</span>}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    onClick={async () => {
                      try {
                        await api.post("/email/temp-invoice-reminder", { temp_invoice_id: r.id, days_overdue: r.days });
                        toast.success(`Reminder email sent to ${r.customer_name}`);
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-primary/40 text-primary text-[11px] font-semibold hover:bg-primary/10"
                    data-testid={`pp-call-email-${r.id}`}
                  >
                    <EnvelopeSimple size={11} weight="fill" /> Email
                  </button>
                  {r.mobile ? (
                    <a href={`tel:${r.mobile}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-destructive/40 text-destructive text-[11px] font-semibold hover:bg-destructive/10">
                      Call
                    </a>
                  ) : null}
                  <a
                    href={(() => {
                      const digits = String(r.mobile || "").replace(/[^0-9]/g, "");
                      const wa = digits.length === 10 ? `91${digits}` : digits;
                      const shareUrl = `${window.location.origin}/p/temp-invoice/${r.id}?t=${r.share_token || ""}`;
                      const text = encodeURIComponent(
                        `Hello ${r.customer_name},\n\nGentle reminder — payment of ₹${Number(r.balance_amount).toFixed(2)} for invoice *${r.number}* is pending since ${r.days} days.\n\nView / Download invoice: ${shareUrl}\n\nKindly clear at your earliest. Thank you!`
                      );
                      return wa ? `https://wa.me/${wa}?text=${text}` : `https://wa.me/?text=${text}`;
                    })()}
                    target="_blank" rel="noopener noreferrer"
                    title={r.mobile ? `WhatsApp → ${r.mobile}` : "No mobile — pick recipient in WhatsApp"}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold hover:opacity-90"
                    style={{ background: "#128C7E", color: "#fff" }}
                    data-testid={`pp-call-whatsapp-${r.id}`}>
                    WhatsApp
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="card-flat p-4 flex items-end gap-3 flex-wrap">
        <div className="space-y-1.5 min-w-[200px] flex-1">
          <Label className="eyebrow">Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer or Temp #…"
            className="h-9" data-testid="tp-search" />
        </div>
        <div className="space-y-1.5 min-w-[200px]">
          <Label className="eyebrow">Customer</Label>
          <Select value={filterCustomerId} onValueChange={setFilterCustomerId}>
            <SelectTrigger className="h-9" data-testid="tp-customer-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 min-w-[180px]">
          <Label className="eyebrow">Product / Service</Label>
          <Input value={filterItem} onChange={(e) => setFilterItem(e.target.value)}
            placeholder="e.g. website" className="h-9" data-testid="tp-item-filter" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">From</Label>
          <Input type="date" min="1900-01-01" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
            className="h-9 font-mono" data-testid="tp-date-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To</Label>
          <Input type="date" min="1900-01-01" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
            className="h-9 font-mono" data-testid="tp-date-to" />
        </div>
        <div className="space-y-1.5 min-w-[160px]">
          <Label className="eyebrow">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9" data-testid="tp-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Fully Paid</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {activeFilterCount > 0 && (
          <Button variant="outline" className="h-9" onClick={resetFilters} data-testid="tp-reset-filters">
            Reset ({activeFilterCount})
          </Button>
        )}
        <div className="text-[11px] text-muted-foreground ml-auto self-center">
          {filteredRows.length} of {rows.length} row{rows.length !== 1 && "s"}
        </div>
      </div>

      {/* Totals bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-flat p-4">
          <div className="eyebrow">Total Pending Value</div>
          <div className="font-heading text-2xl font-bold mt-1">{inr(totals.total)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="eyebrow" style={{ color: "hsl(var(--success))" }}>Received (part-payments)</div>
          <div className="font-heading text-2xl font-bold mt-1" style={{ color: "hsl(var(--success))" }}>{inr(totals.received)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="eyebrow" style={{ color: "hsl(var(--warning))" }}>Outstanding Balance</div>
          <div className="font-heading text-2xl font-bold mt-1" style={{ color: "hsl(var(--warning))" }}>{inr(totals.balance)}</div>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        {selectedIds.size > 0 && (
          <div className={`px-4 py-2.5 flex items-center justify-between gap-3 border-b ${selectionValid ? "bg-primary/5 border-primary/20" : "bg-warning/5 border-warning/30"}`}>
            <div className="text-[12.5px]">
              <span className="font-semibold">{selectedIds.size}</span> selected
              {selectionValid ? (
                <> · <span className="font-medium">{selectedRows[0].customer_name}</span> · Total <span className="font-mono font-semibold">{inr(selectionTotal)}</span>
                {selectedRows.length > 1 && <span className="ml-1 text-muted-foreground">(will be merged into one invoice)</span>}
                </>
              ) : (
                <> · <span style={{ color: "hsl(var(--warning))" }}>All selected must be <b>Fully Paid</b> AND belong to the <b>same customer</b></span></>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={clearSelection}>Clear</Button>
              {can("sales", "create") && (
                <Button size="sm" className="h-7 text-[11px]" onClick={mergeConvert}
                  disabled={!selectionValid} data-testid="tp-merge-convert">
                  <StackSimple size={12} className="mr-1" />
                  {selectedRows.length === 1
                    ? "Convert to Original Invoice"
                    : `Merge & Convert ${selectedRows.length} → Single Invoice`}
                </Button>
              )}
            </div>
          </div>
        )}
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Temp #</th><th>Date</th><th>Customer</th>
              <th className="num" style={{ textAlign: "right" }}>Total</th>
              <th className="num" style={{ textAlign: "right" }}>Received</th>
              <th className="num" style={{ textAlign: "right" }}>Balance</th>
              <th>Status</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">
                {rows.length === 0
                  ? "No pending invoices. Click \"New Pending Invoice\" to record a service delivered but not yet paid."
                  : "No rows match the current filters. Try clearing them."}
              </td></tr>
            ) : filteredRows.map((r) => (
              <tr key={r.id} data-testid={`tp-row-${r.id}`} className={selectedIds.has(r.id) ? "bg-primary/5" : ""}>
                <td>
                  {r.status === "paid" && (
                    <Checkbox
                      checked={selectedIds.has(r.id)}
                      onCheckedChange={() => toggleSelect(r.id)}
                      data-testid={`tp-select-${r.id}`}
                      aria-label={`Select ${r.number}`}
                    />
                  )}
                </td>
                <td className="font-mono text-[12.5px] font-semibold">
                  {r.number}
                  {r.converted_invoice_number && r.converted_invoice_id && (
                    <Link
                      to={`/sales/${r.converted_invoice_id}`}
                      className="block text-[10px] text-primary mt-0.5 hover:underline"
                      data-testid={`tp-open-inv-${r.id}`}
                      title="Open the generated Sales Invoice"
                    >
                      → {r.converted_invoice_number}
                    </Link>
                  )}
                </td>
                <td className="font-mono text-[12px]">{fmtDate(r.date)}</td>
                <td className="font-medium">
                  {r.customer_name}
                  {Array.isArray(r.supporting_tenders) && r.supporting_tenders.length > 0 && (
                    <div className="text-[10.5px] text-muted-foreground mt-0.5" title={r.supporting_tenders.join("\n")}>
                      <span className="uppercase tracking-wider text-[9px] mr-1 text-primary/70">Supp:</span>
                      {r.supporting_tenders.join(", ")}
                    </div>
                  )}
                </td>
                <td className="num" style={{ textAlign: "right" }}>{inr(r.grand_total)}</td>
                <td className="num" style={{ textAlign: "right", color: "hsl(var(--success))" }}>{inr(r.received_amount)}</td>
                <td className="num" style={{ textAlign: "right", color: (r.balance_amount > 0 ? "hsl(var(--warning))" : "inherit") }}>{inr(r.balance_amount)}</td>
                <td><StatusPill status={r.status} /></td>
                <td className="num" style={{ textAlign: "right" }}>
                  <div className="flex gap-1 justify-end">
                    {r.status !== "converted" && r.balance_amount > 0 && can("sales", "edit") && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] px-2"
                        onClick={() => openReceipt(r)} data-testid={`tp-add-receipt-${r.id}`}
                        title="Record a payment">
                        <Money size={12} className="mr-1" /> Add Receipt
                      </Button>
                    )}
                    {r.status === "paid" && can("sales", "create") && (
                      <Button size="sm" className="h-7 text-[11px] px-2"
                        onClick={() => convertToInvoice(r)} data-testid={`tp-convert-${r.id}`}
                        title="Fully paid — convert to real invoice">
                        <Receipt size={12} className="mr-1" /> Convert
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      title="Print / Share (QR Pay + Print)"
                      onClick={() => window.open(`/p/temp-invoice/${r.id}?t=${encodeURIComponent(r.share_token || "")}&autoprint=1`, "_blank", "noopener")}
                      data-testid={`tp-print-${r.id}`}>
                      <Printer size={13} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                      style={{ color: "#128C7E" }}
                      title={r.mobile ? `WhatsApp payment reminder + invoice link → ${r.mobile}` : "No mobile on file — will open WhatsApp so you can pick the recipient"}
                      onClick={() => {
                        const phone = String(r.mobile || "").replace(/[^0-9]/g, "");
                        const waPhone = phone.length === 10 ? `91${phone}` : phone;
                        const shareUrl = `${window.location.origin}/p/temp-invoice/${r.id}?t=${r.share_token || ""}`;
                        const msg = `Hello ${r.customer_name},\n\n₹${Number(r.balance_amount).toFixed(2)} is pending for invoice *${r.number}* dated ${fmtDate(r.date)}.\n\nView / Download invoice: ${shareUrl}\n\nKindly clear at your earliest. Thank you!`;
                        const url = waPhone
                          ? `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`
                          : `https://wa.me/?text=${encodeURIComponent(msg)}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                        if (!phone) toast.info("Pick the recipient inside WhatsApp — no mobile saved for this customer.", { duration: 5000 });
                      }}
                      data-testid={`tp-whatsapp-${r.id}`}>
                      <WhatsappLogo size={15} weight="fill" style={{ color: "#128C7E" }} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                      title="Email payment reminder"
                      onClick={async () => {
                        try {
                          await api.post("/email/temp-invoice-reminder", { temp_invoice_id: r.id });
                          toast.success(`Emailed to ${r.customer_name}`);
                        } catch (err) { toast.error(formatApiError(err)); }
                      }}
                      data-testid={`tp-email-${r.id}`}>
                      <EnvelopeSimple size={14} />
                    </Button>
                    {r.status === "converted" && r.converted_invoice_id && user?.role === "admin" && (
                      <Link to={`/sales/${r.converted_invoice_id}`}>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] px-2"
                          data-testid={`tp-edit-inv-${r.id}`}
                          title={`Admin override — edit ${r.converted_invoice_number || "Sales Invoice"}`}>
                          <PencilSimple size={12} className="mr-1" /> Edit Invoice
                        </Button>
                      </Link>
                    )}
                    {r.status === "converted" && r.converted_invoice_id && user?.role !== "admin" && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground px-2 h-7"
                        data-testid={`tp-locked-inv-${r.id}`}
                        title="Auto-generated invoice — only admins can edit">
                        <Lock size={11} weight="fill" /> Invoice locked
                      </span>
                    )}
                    {r.status !== "converted" && can("sales", "edit") && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => startEdit(r)} data-testid={`tp-edit-${r.id}`}>
                        <PencilSimple size={13} />
                      </Button>
                    )}
                    {user?.role === "admin" && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                        onClick={() => del(r)} data-testid={`tp-del-${r.id}`}
                        title={r.status === "converted"
                          ? "Delete this + its converted sales invoice (admin)"
                          : "Delete this pending invoice (admin)"}>
                        <Trash size={13} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing ? `Edit ${editing.number}` : "New Pending Invoice"}
            </DialogTitle>
            <DialogDescription>
              Temporary invoice starts as <span className="font-mono">VT/TEMP/nnnn</span>. Once fully paid, convert to a real sales invoice.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="eyebrow">Date *</Label>
                <Input type="date" value={form.date} min={dateFloor}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="font-mono" data-testid="tp-form-date" required />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="eyebrow">Customer *</Label>
                <SearchableCustomerSelect
                  value={form.customer_id}
                  onChange={pickCustomer}
                  customers={customers}
                  placeholder="Search & pick customer"
                  testid="tp-form-customer"
                  getSubtitle={(c) => c.gstin || c.phone}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="eyebrow">Line Items *</Label>
              <LineItemsEditor
                items={form.items}
                setItems={(items) => setForm((f) => ({ ...f, items }))}
                products={productOptions}
                intraState={intraState}
                gstEnabled={isGst}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="eyebrow">Notes</Label>
              <Textarea rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="E.g. Website deployment done on 08 Feb — pending payment" />
            </div>

            <SupportingTendersEditor
              value={form.supporting_tenders}
              onChange={(list) => setForm((f) => ({ ...f, supporting_tenders: list }))}
              testIdPrefix="tp-supporting-tender"
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="tp-form-save">{editing ? "Save" : "Create Temp Invoice"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Receipt dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Add Receipt · {receiptTarget?.number}</DialogTitle>
            <DialogDescription>
              Customer: <span className="font-medium">{receiptTarget?.customer_name}</span> ·
              Balance: <span className="font-mono font-medium" style={{ color: "hsl(var(--warning))" }}>{inr(receiptTarget?.balance_amount || 0)}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReceipt} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="eyebrow">Date *</Label>
                <Input type="date" value={receiptForm.date} min={dateFloor}
                  onChange={(e) => setReceiptForm({ ...receiptForm, date: e.target.value })}
                  className="font-mono" required />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Amount *</Label>
                <Input type="number" step="0.01" min={0.01} value={receiptForm.amount}
                  onChange={(e) => setReceiptForm({ ...receiptForm, amount: Number(e.target.value) })}
                  className="font-mono" required data-testid="tp-receipt-amount" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Mode</Label>
                <Select value={receiptForm.mode} onValueChange={(v) => setReceiptForm({ ...receiptForm, mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="rtgs_neft">RTGS / NEFT</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="debit_card">Debit Card</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">UPI ID / Transaction ID / UTR</Label>
                <Input value={receiptForm.reference}
                  onChange={(e) => setReceiptForm({ ...receiptForm, reference: e.target.value })}
                  placeholder="UPI ID or Transaction ID / UTR No" className="font-mono" />
              </div>
            </div>
            {receiptTarget && receiptForm.amount >= (receiptTarget.balance_amount - 0.005) && (
              <div className="rounded-md border border-success/30 bg-success/5 p-2 text-[12px] flex items-center gap-2" style={{ color: "hsl(var(--success))" }}>
                <CheckCircle size={14} weight="fill" />
                This payment settles the full balance. You'll be able to convert to a real invoice next.
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReceiptOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="tp-receipt-save">Record Payment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Merge & (optionally) Pay dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {selectedRows.length === 1 ? "Convert to Original Invoice" : `Merge ${selectedRows.length} Temp Invoices`}
            </DialogTitle>
            <DialogDescription>
              Customer: <span className="font-medium">{selectedRows[0]?.customer_name}</span> ·
              Total <span className="font-mono font-semibold">{inr(selectionTotal)}</span>
              {selectionBalance > 0.005 && (
                <> · Outstanding <span className="font-mono font-semibold" style={{ color: "hsl(var(--warning))" }}>{inr(selectionBalance)}</span></>
              )}
            </DialogDescription>
          </DialogHeader>

          {!allPaid ? (
            <div className="space-y-3">
              <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-[12.5px]" style={{ color: "hsl(var(--warning))" }}>
                Not all selected temps are fully paid. Enter payment details below to record the outstanding
                <b> {inr(selectionBalance)}</b> as one consolidated receipt across all selected temps, then convert.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="eyebrow">Payment Date</Label>
                  <Input type="date" value={mergeForm.payment_date} min={dateFloor}
                    onChange={(e) => setMergeForm({ ...mergeForm, payment_date: e.target.value })}
                    className="font-mono" data-testid="tp-merge-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Mode</Label>
                  <Select value={mergeForm.payment_mode} onValueChange={(v) => setMergeForm({ ...mergeForm, payment_mode: v })}>
                    <SelectTrigger data-testid="tp-merge-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="rtgs_neft">RTGS / NEFT</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="debit_card">Debit Card</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="eyebrow">UPI ID / Transaction ID / UTR</Label>
                  <Input value={mergeForm.payment_reference}
                    onChange={(e) => setMergeForm({ ...mergeForm, payment_reference: e.target.value })}
                    placeholder="UPI ID or Transaction ID / UTR No" className="font-mono" data-testid="tp-merge-ref" />
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-success/40 bg-success/5 p-3 text-[12.5px]" style={{ color: "hsl(var(--success))" }}>
              All selected temps are already fully paid. Ready to merge into a single Sales Invoice.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button onClick={runMerge} data-testid="tp-merge-run">
              {allPaid ? "Merge & Convert" : `Pay ${inr(selectionBalance)} & Convert`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
